import { makeAutoObservable } from 'mobx'
import dayjs from 'dayjs'
import type { ApiErrors } from 'shared/api'
import { type IDropdownItem, EnumDropdownItemVariant } from 'shared/ui'
import { type IResponseIntegrationStateAccount } from 'entities/Integrations'
import {
  type IBookingAvailabilityDayKey,
  type IBookingAvailabilityRange,
  type ChatbotBooking,
  BookingAvailabilityDayKey,
  DEFAULT_BOOKING_DURATIONS,
} from 'entities/Chatbot'
import type { GoogleCalendarIntegrationStore } from './googleCalendarIntegrationStore'

const AVAILABILITY_DAYS: { key: IBookingAvailabilityDayKey; label: string; isDefault: boolean }[] =
  [
    { key: BookingAvailabilityDayKey.Sun, label: 'Sun', isDefault: false },
    { key: BookingAvailabilityDayKey.Mon, label: 'Mon', isDefault: true },
    { key: BookingAvailabilityDayKey.Tue, label: 'Tue', isDefault: true },
    { key: BookingAvailabilityDayKey.Wed, label: 'Wed', isDefault: true },
    { key: BookingAvailabilityDayKey.Thu, label: 'Thu', isDefault: true },
    { key: BookingAvailabilityDayKey.Fri, label: 'Fri', isDefault: true },
    { key: BookingAvailabilityDayKey.Sat, label: 'Sat', isDefault: false },
  ]

const DEFAULT_AVAILABILITY_DAYS = AVAILABILITY_DAYS.filter((day) => day.isDefault).map(
  (day) => day.key
)

const DEFAULT_AVAILABILITY = '09:00'

export type IGoogleCalendarBookingDraft = {
  googleCalendar: ChatbotBooking['googleCalendar']
  availability: ChatbotBooking['availability']
  durations: ChatbotBooking['durations']
  setGoogleCalendar: (value: ChatbotBooking['googleCalendar']) => void
  setAvailability: (value: ChatbotBooking['availability']) => void
  setDurations: (value: ChatbotBooking['durations']) => void
}

type IGoogleCalendarErrorKey =
  | 'googleCalendarError'
  | 'availabilityDaysError'
  | 'availabilityTimeError'
  | 'durationsError'

export class GoogleCalendarPickerStore {
  private _googleCalendarError = ''
  private _availabilityDaysError = ''
  private _availabilityTimeError = ''
  private _durationsError = ''
  private _availabilityFrom: string
  private _availabilityTo: string

  constructor(
    private _integration: GoogleCalendarIntegrationStore,
    private _booking: IGoogleCalendarBookingDraft
  ) {
    this._availabilityFrom = this._getSharedAvailabilityTime('from') ?? DEFAULT_AVAILABILITY
    this._availabilityTo = this._getSharedAvailabilityTime('to') ?? DEFAULT_AVAILABILITY
    makeAutoObservable(this)
  }

  get accounts() {
    return this._integration.accounts
  }

  get loading() {
    return this._integration.loading
  }

  get isGoogleAccountConnecting() {
    return this._integration.isGoogleAccountConnecting
  }

  get search() {
    return this._integration.search
  }

  get hasAccounts() {
    return this._integration.hasAccounts
  }

  setSearch = (value: string) => {
    this._integration.setSearch(value)
  }

  connectAccount = () => this._integration.connectAccount()

  get googleCalendarError() {
    return this._googleCalendarError
  }

  get availabilityDaysError() {
    return this._availabilityDaysError
  }

  get availabilityTimeError() {
    return this._availabilityTimeError
  }

  get durationsError() {
    return this._durationsError
  }

  get availabilityDaysOrder() {
    return AVAILABILITY_DAYS.map((day) => day.key)
  }

  getAvailabilityDayLabel = (day: IBookingAvailabilityDayKey) =>
    Object.fromEntries(AVAILABILITY_DAYS.map((day) => [day.key, day.label]))[day]

  get durationOptions() {
    return [15, 30, 45, 60]
  }

  get availabilityDays() {
    const value = this._booking.availability?.value
    if (!value) return new Set(DEFAULT_AVAILABILITY_DAYS)
    return new Set(this.availabilityDaysOrder.filter((day) => (value[day]?.length ?? 0) > 0))
  }

  get availabilityFrom() {
    return this._availabilityFrom
  }

  get availabilityTo() {
    return this._availabilityTo
  }

  get availabilityTimezone() {
    return this._booking.availability?.timezone ?? dayjs.tz.guess()
  }

  private _timeStringToDate = (time: string) => {
    return dayjs(`${dayjs().format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD HH:mm').toDate()
  }

  get availabilityFromDate() {
    return this._timeStringToDate(this.availabilityFrom)
  }

  get availabilityToDate() {
    return this._timeStringToDate(this.availabilityTo)
  }

  get durations() {
    return this._booking.durations ?? DEFAULT_BOOKING_DURATIONS
  }

  get selectedCalendar() {
    const value = this._booking.googleCalendar
    if (!value) return null
    const accountCalendars = this._integration.calendarsForAccount(value.account_id)
    return accountCalendars.find((calendar) => calendar.id === value.value) ?? null
  }

  get selectedCalendarItem(): IDropdownItem | null {
    const calendar = this.selectedCalendar
    if (!calendar) return null
    return {
      id: calendar.id,
      label: calendar.name,
      labelRight: calendar.id,
      labelRightVariant: 'body-md-regular',
    }
  }

  private _calendarItemsForAccount = (
    account: IResponseIntegrationStateAccount
  ): IDropdownItem[] => {
    const search = this.search.trim().toLowerCase()
    const calendars = this._integration.calendarsForAccount(account.external_user_id)

    return calendars
      .filter((calendar) => {
        if (!search) return true
        return (
          calendar.name.toLowerCase().includes(search) || calendar.id.toLowerCase().includes(search)
        )
      })
      .map((calendar) => {
        const disabled = !calendar.is_bookable
        const reason = !calendar.meet_enabled
          ? 'This calendar cannot host Google Meet meetings.'
          : calendar.access_role === 'reader' || calendar.access_role === 'freeBusyReader'
          ? 'This account can’t add events to this calendar.'
          : 'This calendar can’t be used for booking.'

        const isSelected =
          this._booking.googleCalendar?.value === calendar.id &&
          this._booking.googleCalendar?.account_id === account.external_user_id

        return {
          id: calendar.id,
          group: account.email,
          label: calendar.name,
          variant: EnumDropdownItemVariant.CheckedRight,
          activeValue: isSelected,
          disabled,
          data: { accountId: account.external_user_id },
          ...(disabled && {
            tooltipProps: {
              label: reason,
              placement: 'top',
              breakWord: true,
            },
          }),
        }
      })
  }

  get calendarGroups() {
    return this._integration.accounts.map((account) => ({
      account,
      items: this._calendarItemsForAccount(account),
    }))
  }

  setGoogleCalendar = (item: IDropdownItem) => {
    const accountId = item.data?.accountId
    if (!accountId) return

    const calendar = this._integration
      .calendarsForAccount(accountId)
      .find((calendar) => calendar.id === item.id)
    if (!calendar) return

    this._booking.setGoogleCalendar({
      value: calendar.id,
      account_id: accountId,
      meet_enabled: calendar.meet_enabled,
    })
    this._googleCalendarError = ''

    if (!this._booking.availability?.timezone) {
      this._booking.setAvailability({
        value: this._booking.availability?.value ?? this.buildDefaultAvailabilityValue(),
        timezone: calendar.timezone,
      })
    }
  }

  buildDefaultAvailabilityValue = () => {
    const value: Partial<Record<IBookingAvailabilityDayKey, IBookingAvailabilityRange[]>> = {}
    DEFAULT_AVAILABILITY_DAYS.forEach((day) => {
      value[day] = [{ from: DEFAULT_AVAILABILITY, to: DEFAULT_AVAILABILITY }]
    })
    return value
  }

  setAvailabilityFromDate = (date: Date | null) => {
    if (!date) return
    this.setAvailabilityFrom(dayjs(date).format('HH:mm'))
  }

  setAvailabilityToDate = (date: Date | null) => {
    if (!date) return
    this.setAvailabilityTo(dayjs(date).format('HH:mm'))
  }

  private _getSharedAvailabilityTime = (field: 'from' | 'to') => {
    const value = this._booking.availability?.value
    if (!value) return null
    for (const day of this.availabilityDaysOrder) {
      const range = value[day]?.[0]
      if (range) return range[field]
    }
    return null
  }

  private _writeAvailability = (
    days: Set<IBookingAvailabilityDayKey>,
    from: string,
    to: string,
    timezone?: string
  ) => {
    const value: Partial<Record<IBookingAvailabilityDayKey, { from: string; to: string }[]>> = {}
    this.availabilityDaysOrder.forEach((day) => {
      if (days.has(day)) {
        value[day] = [{ from, to }]
      }
    })

    this._booking.setAvailability({
      value,
      timezone: timezone ?? this.availabilityTimezone,
    })
  }

  toggleAvailabilityDay = (day: IBookingAvailabilityDayKey) => {
    const days = this.availabilityDays
    if (days.has(day)) {
      days.delete(day)
    } else {
      days.add(day)
    }
    this._availabilityDaysError = ''
    this._writeAvailability(days, this.availabilityFrom, this.availabilityTo)
  }

  setAvailabilityFrom = (time: string) => {
    this._availabilityFrom = time
    this._availabilityTimeError = ''
    this._writeAvailability(this.availabilityDays, time, this.availabilityTo)
  }

  setAvailabilityTo = (time: string) => {
    this._availabilityTo = time
    this._availabilityTimeError = ''
    this._writeAvailability(this.availabilityDays, this.availabilityFrom, time)
  }

  setAvailabilityTimezone = (timezone: string) => {
    this._writeAvailability(
      this.availabilityDays,
      this.availabilityFrom,
      this.availabilityTo,
      timezone
    )
  }

  toggleDuration = (minutes: number) => {
    const durations = this.durations
    this._durationsError = ''
    if (durations.includes(minutes)) {
      this._booking.setDurations(durations.filter((value) => value !== minutes))
    } else {
      this._booking.setDurations([...durations, minutes].sort((a, b) => a - b))
    }
  }

  private get _availabilityRanges() {
    const value = this._booking.availability?.value
    if (!value) return []
    return this.availabilityDaysOrder.flatMap((day) => value[day] ?? [])
  }

  validateCalendar = () => {
    this._googleCalendarError = ''
    if (!this._booking.googleCalendar?.value || !this._booking.googleCalendar?.account_id) {
      this._googleCalendarError = 'Please select the calendar to book meetings on.'
      return false
    }
    return true
  }

  validateAvailabilityDays = () => {
    this._availabilityDaysError = ''
    if (this._availabilityRanges.length === 0) {
      this._availabilityDaysError =
        'Please select at least one day the agent can offer meetings on.'
      return false
    }
    return true
  }

  validateAvailabilityTime = () => {
    this._availabilityTimeError = ''
    if (this.availabilityFrom >= this.availabilityTo) {
      this._availabilityTimeError = 'The end time must be after the start time.'
      return false
    }
    return true
  }

  validateDurations = () => {
    this._durationsError = ''
    if (this.durations.length === 0) {
      this._durationsError = 'Please select at least one meeting duration.'
      return false
    }
    return true
  }

  validate = () => {
    const isCalendarValid = this.validateCalendar()
    const areDaysValid = this.validateAvailabilityDays()
    const isTimeValid = this.validateAvailabilityTime()
    const areDurationsValid = this.validateDurations()

    return isCalendarValid && areDaysValid && isTimeValid && areDurationsValid
  }

  handleErrors = (errors: ApiErrors) => {
    const googleCalendarError =
      errors.getError('settings.google_calendar.value') ||
      errors.getError('settings.google_calendar.account_id')
    const availabilityDaysError = errors.getError('settings.booking_availability.value')
    const availabilityTimeError = errors.getError('settings.booking_availability.timezone')
    const durationsError = errors.getError('settings.booking_durations.value')

    if (googleCalendarError) this._googleCalendarError = googleCalendarError
    if (availabilityDaysError) this._availabilityDaysError = availabilityDaysError
    if (availabilityTimeError) this._availabilityTimeError = availabilityTimeError
    if (durationsError) this._durationsError = durationsError
  }

  resetErrors = () => {
    this._googleCalendarError = ''
    this._availabilityDaysError = ''
    this._availabilityTimeError = ''
    this._durationsError = ''
  }

  resetError = (key: IGoogleCalendarErrorKey) => {
    switch (key) {
      case 'googleCalendarError':
        this._googleCalendarError = ''
        break
    }
  }

  resetBookingData = () => {
    this._booking.setGoogleCalendar(null)
    this._booking.setAvailability(null)
    this._booking.setDurations(null)
    this._availabilityFrom = DEFAULT_AVAILABILITY
    this._availabilityTo = DEFAULT_AVAILABILITY
  }

  restoreAvailabilityTime = () => {
    this._availabilityFrom = this._getSharedAvailabilityTime('from') ?? DEFAULT_AVAILABILITY
    this._availabilityTo = this._getSharedAvailabilityTime('to') ?? DEFAULT_AVAILABILITY
  }
}
